"use client";

import * as React from "react";
import {
  buildCloudinaryDprSrcSet,
  buildCloudinarySrcSet,
  withCloudinaryTransform,
} from "./cloudinary";
import type { ImageProviderComponentProps } from "./image-provider";
import { sanitizeAlt, useImage } from "./use-image";

// Global override mechanism via context so apps (e.g. b2b-portal) can provide Next.js Image
// without adding a next/image dependency to the ui package.
// The override component must accept the base ImageProps defined below.
// WHY: This avoids coupling while enabling advanced image optimization features in consuming apps.

// Default blur placeholder for better loading experience
const blurDataURLDefault =
  "data:image/webp;base64,UklGRo4CAABXRUJQVlA4IIICAAAQKgCdASp3AfoAPpFInkulpKKho71oILASCWlu4XVRCPNpCJbwVQN+IIaWsb8Zo434zRxvxmjjfjNHG/GaON+M0cb8Zo434zRxvxmjjfjNHG/GaON+M0cb8ZomePP8ODRlzb/YOZni5Gcmj8srDPfHluhGz6gTs24ruEWcizaN+uQMUstDHFp+JHD0p/5UN2HOiZ2SKmnGUiLX1Eyrf/nr3aUWGB5avMZiQfhl3qkk2XBg0lAXsVL9TkWn4icY6okvhOvAAoBSh0hFjQeVD3PSzS2YD1E0e3Y0N7ozFBUt6QJZw24uhZGTOr5Q9ZiKn6y71SNQ1qg4tPxj3XIaMkOxsjwoGz1fDn5dkyKFxo48GDeWsb8Si3V4pseA8tGtcpOtqw1PYzE/GaON+NSVPxqnBzYHlrG/GaON+M0cb8Zo434zRxvxmjjfjNHG/GaON+M0cb8Zo434zKgA/v7fuTtGx44AAAAAAArRHe040lnx/brHO5gLQxTurpq4s3vGazXsQFvSqHPqoI9BhrFC2a0mPSx2N5UWIo0FapPdes2GdjzThSl8M9kzLCFSkhG+c39OQIUR3Uy+H6png8dNk18pq5fcF9OGodLLcSOOq6K47j/V0L040oeYBYSe/El4BnSkGaJsrF6R508BbJPOqFwmg8Q52QVTPoL2lNYDoT7RKuJLo51qB2ziCFFsbaH0yVC4AsDm002VdCvBVLvF5c/Q7NIVttJBazlaj90txmOBx0ATzd/BCV6TNtWO1GXHTgu2ZHcnzpIJ/JjDn8bdNv5gBDLPmFYfzq65Q+191AedUbfixAtR0LS0lVEzk3ig+9xskURR5IA6DBIDhdFBvmAAAAA=";

export type AspectRatioSquare = "1:1";
export type AspectRatioFourThree = "4:3";
export type AspectRatioSixteenNine = "16:9";
export type AspectRatioSixteenTen = "16:10";
export type AspectRatioNineSixteen = "9:16";
export type AspectRatioThreeFour = "3:4";
export type AspectRatioTwoThree = "2:3";
export type AspectRatioA4 = "1:1.414";
export type AspectRatioNineEleven = "9:11";
export type AspectRatioThreeOne = "3:1";

interface BaseImageProps
  extends Omit<
    React.ComponentProps<"img">,
    "width" | "height" | "loading" | "onError" | "onLoad" | "sizes"
  > {
  src: string;
  alt: string;
  /** Border radius token - maps to Tailwind rounded-* classes. */
  borderRadius?: "none" | "sm" | "md" | "lg" | "full";
  loading?: "lazy" | "eager";
  unStyled?: boolean;
  onError?: () => void;
  onLoad?: () => void;
  onLoadingComplete?: () => void;
  quality?: number;
  priority?: boolean;
  placeholder?: "blur" | "empty";
  blurDataURL?: string;
  sizes?: string;
  decorative?: boolean;
  decorativeLabel?: string;
  aspectRatio:
    | AspectRatioSquare
    | AspectRatioFourThree
    | AspectRatioSixteenNine
    | AspectRatioSixteenTen
    | AspectRatioNineSixteen
    | AspectRatioThreeFour
    | AspectRatioTwoThree
    | AspectRatioNineEleven
    | AspectRatioA4
    | AspectRatioThreeOne;
  /** CSS `object-position` value. Only allowed keywords for consistency. */
  objectPosition?:
    | "center"
    | "top"
    | "bottom"
    | "left"
    | "right"
    | "top left"
    | "top right"
    | "bottom left"
    | "bottom right";
}

interface ImageSizeProps extends BaseImageProps {
  fill?: false;
  width?: number;
  height?: number;
  objectFit?: never;
}

interface ImageFillProps extends BaseImageProps {
  fill?: true;
  width?: never;
  height?: never;
  objectFit?: "contain" | "cover" | "none";
}

export type ImageProps = ImageSizeProps | ImageFillProps;

const warnedMissingSizes = new Set<string>();

const WIDTH_DESCRIPTOR = /^(\d+)w$/;

/**
 * Fill mode without `sizes` falls back to "100vw", which tells the browser the image
 * spans the whole viewport. For anything smaller — a 321px card, a 64px thumbnail —
 * the browser then picks a far larger candidate than it paints. Only the call site
 * knows the real rendered width, so warn rather than guess a different default.
 */
function warnMissingSizes(src: string): void {
  if (process.env.NODE_ENV === "production" || warnedMissingSizes.has(src)) {
    return;
  }

  warnedMissingSizes.add(src);
  // biome-ignore lint/suspicious/noConsole: development-only performance warning
  console.warn(
    "<Image> renders in fill mode without a `sizes` prop, so the browser assumes " +
      '"100vw" and downloads a larger file than it displays. Pass `sizes` describing ' +
      'the rendered width, e.g. sizes="(min-width: 768px) 320px, 50vw".\n  src: ' +
      src
  );
}

/**
 * The provider (next/image) builds its own srcSet and drops the caller's, so CMS
 * candidates would be lost and `src` re-encoded. Handing it a loader that maps each
 * requested width to the smallest candidate covering it keeps those URLs while the
 * provider still handles placeholder and priority.
 */
function createSrcSetLoader(
  srcSet: string
): ImageProviderComponentProps["loader"] {
  // Split on ", " only: Cloudinary URLs contain bare commas (`f_auto,q_auto`).
  const candidates = srcSet
    .split(/,\s+/)
    .map((candidate) => {
      const [url, descriptor = ""] = candidate.trim().split(/\s+/);
      const width = WIDTH_DESCRIPTOR.exec(descriptor)?.[1];
      return { url, width: width ? Number(width) : 0 };
    })
    .filter((candidate) => candidate.url && candidate.width > 0)
    .sort((a, b) => a.width - b.width);

  const largest = candidates.at(-1);
  if (!largest) {
    return;
  }

  return ({ width }) =>
    (candidates.find((candidate) => candidate.width >= width) ?? largest).url;
}

export function Image(props: ImageProps): React.ReactNode {
  const Comp = useImage() || "img";
  const isNativeImg = Comp === "img";
  const aspectRatio = props.aspectRatio;
  const [loadError, setLoadError] = React.useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: props.src is a reset trigger; setLoadError is stable and intentionally not listed
  React.useEffect(() => {
    setLoadError(false);
  }, [props.src]);

  const blurDataURL = props.blurDataURL || blurDataURLDefault;
  const placeholderValue: "blur" | "empty" | undefined =
    props.placeholder || "blur";
  const loading = props.priority ? "eager" : (props.loading ?? "lazy");
  const fallbackSizes =
    props.sizes || (props.width ? `${props.width}px` : "100vw");
  const sizesWithBreakpoints =
    loading === "lazy" ? `auto, ${fallbackSizes}` : fallbackSizes;

  // Cloudinary serves the original asset (often 5000px+) unless the URL asks for a
  // smaller one, so derive a width-capped src/srcSet for the native <img> paths.
  // Non-Cloudinary sources fall through untouched.
  // WHY the provider path keeps props.src: baking a width into next/image's source
  // caps what it can generate and makes it upscale on large screens. That path needs
  // a Cloudinary loader instead.
  const optimizedSrc = loadError
    ? blurDataURL
    : withCloudinaryTransform(props.src, props.width);
  // Only the native paths render this, so the provider path skips building the ladder.
  let optimizedSrcSet: string | undefined;
  if (isNativeImg && !loadError) {
    optimizedSrcSet =
      props.srcSet ??
      (props.width
        ? buildCloudinaryDprSrcSet(props.src, props.width)
        : buildCloudinarySrcSet(props.src));
  }
  const providerLoader =
    !(isNativeImg || loadError) && props.srcSet
      ? createSrcSetLoader(props.srcSet)
      : undefined;

  // Default to fill mode unless width/height are explicitly provided
  const hasFillProp = "fill" in props;
  const hasExplicitSize = Boolean(props.width) || Boolean(props.height);
  const useFillMode = hasFillProp ? props.fill !== false : !hasExplicitSize;

  let roundedClass = "";
  switch (props.borderRadius) {
    case "none":
      roundedClass = "rounded-none";
      break;
    case "sm":
      roundedClass = "rounded-sm";
      break;
    case "md":
      roundedClass = "rounded-md";
      break;
    case "lg":
      roundedClass = "rounded-lg";
      break;
    case "full":
      roundedClass = "rounded-full";
      break;
    default:
      break;
  }

  const combinedClassName = [
    // prefer props.className first so callers can override
    props.className,
    roundedClass,
  ]
    .filter(Boolean)
    .join(" ");

  // Unstyled mode
  if (props.unStyled) {
    return (
      <img
        alt={sanitizeAlt(
          props.alt,
          Boolean(props.decorative),
          props.decorativeLabel
        )}
        aria-hidden={props.decorative ? true : undefined}
        className={props.className}
        draggable={props.draggable}
        onClick={props.onClick}
        onMouseDown={props.onMouseDown}
        src={optimizedSrc}
        style={props.style}
      />
    );
  }

  // Handle fill mode with aspect ratio wrapper
  if (useFillMode) {
    if (!props.sizes) {
      warnMissingSizes(props.src);
    }

    return (
      <div
        className={combinedClassName}
        data-testid="image-fill-wrapper"
        style={{
          aspectRatio: aspectRatio.replace(":", "/"),
          position: "relative",
          width: "100%",
          height: "100%",
        }}
      >
        {isNativeImg ? (
          <img
            alt={sanitizeAlt(
              props.alt,
              Boolean(props.decorative),
              props.decorativeLabel
            )}
            aria-hidden={props.decorative ? true : undefined}
            className={combinedClassName}
            loading={loading}
            onClick={props.onClick}
            onError={() => {
              setLoadError(true);
              props.onError?.();
            }}
            onLoad={props.onLoad}
            sizes={optimizedSrcSet ? sizesWithBreakpoints : undefined}
            src={optimizedSrc}
            srcSet={optimizedSrcSet}
            style={{
              objectFit: props.objectFit || "cover",
              objectPosition: props.objectPosition || "center",
              width: "100%",
              height: "100%",
            }}
          />
        ) : (
          <Comp
            alt={sanitizeAlt(
              props.alt,
              Boolean(props.decorative),
              props.decorativeLabel
            )}
            aria-hidden={props.decorative ? true : undefined}
            blurDataURL={blurDataURL}
            className={combinedClassName}
            fill={true}
            loader={providerLoader}
            loading={loading}
            onClick={props.onClick}
            onError={() => {
              setLoadError(true);
              props.onError?.();
            }}
            onLoad={props.onLoad}
            onLoadingComplete={props.onLoadingComplete}
            placeholder={loadError ? "empty" : placeholderValue}
            priority={props.priority}
            quality={props.quality}
            sizes={sizesWithBreakpoints}
            src={loadError ? blurDataURL : props.src}
            style={{
              objectFit: props.objectFit || "cover",
              objectPosition: props.objectPosition || "center",
            }}
          />
        )}
      </div>
    );
  }

  // Standard size mode
  if (isNativeImg) {
    return (
      <img
        alt={sanitizeAlt(
          props.alt,
          Boolean(props.decorative),
          props.decorativeLabel
        )}
        aria-hidden={props.decorative ? true : undefined}
        className={combinedClassName}
        height={props.height}
        loading={loading}
        onClick={props.onClick}
        onError={() => {
          setLoadError(true);
          props.onError?.();
        }}
        onLoad={props.onLoad}
        sizes={props.srcSet ? sizesWithBreakpoints : undefined}
        src={optimizedSrc}
        srcSet={optimizedSrcSet}
        width={props.width}
      />
    );
  }

  return (
    <Comp
      alt={sanitizeAlt(
        props.alt,
        Boolean(props.decorative),
        props.decorativeLabel
      )}
      aria-hidden={props.decorative ? true : undefined}
      blurDataURL={blurDataURL}
      className={combinedClassName}
      height={props.height}
      loader={providerLoader}
      loading={loading}
      onClick={props.onClick}
      onError={() => {
        setLoadError(true);
        props.onError?.();
      }}
      onLoad={props.onLoad}
      onLoadingComplete={props.onLoadingComplete}
      placeholder={loadError ? "empty" : placeholderValue}
      priority={props.priority}
      quality={props.quality}
      sizes={sizesWithBreakpoints}
      src={loadError ? blurDataURL : props.src}
      width={props.width}
    />
  );
}
